/**
 * 记住密码管理工具类
 * 提供安全的密码存储和读取功能
 */

import Cookies from 'js-cookie'

interface RememberedCredentials {
  account: string
  password: string
  timestamp: number
}

/**
 * 简单的字符串加密解密（Base64 + 简单偏移）
 * 注意：这只是防止明文存储，不是安全的加密方式
 */
class SimpleEncryption {
  private static readonly OFFSET = 7
  private static readonly SECRET = 'CT_REMEMBER_PWD'

  /**
   * 加密字符串
   */
  static encrypt(text: string): string {
    // 先进行字符偏移
    const shifted = text
      .split('')
      .map(char => String.fromCharCode(char.charCodeAt(0) + this.OFFSET))
      .join('')

    // 再用Base64编码
    const withSecret = this.SECRET + shifted
    return btoa(encodeURIComponent(withSecret))
  }

  /**
   * 解密字符串
   */
  static decrypt(encryptedText: string): string {
    try {
      // Base64解码
      const withSecret = decodeURIComponent(atob(encryptedText))

      // 移除密钥前缀
      if (!withSecret.startsWith(this.SECRET)) {
        throw new Error('Invalid encrypted data')
      }

      const shifted = withSecret.substring(this.SECRET.length)

      // 字符偏移还原
      return shifted
        .split('')
        .map(char => String.fromCharCode(char.charCodeAt(0) - this.OFFSET))
        .join('')
    } catch (error) {
      console.warn('[CT_REMEMBER_PWD] 解密失败:', error)
      return ''
    }
  }
}

/**
 * 记住密码管理器
 */
export class RememberPasswordManager {
  private static readonly BASE_COOKIE_KEY = 'ct_remembered_credentials'
  private static readonly EXPIRE_DAYS = 30 // 30天过期
  private static readonly MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30天毫秒数

  /**
   * 根据来源生成Cookie键名
   * @param sourceFrom 来源标识
   */
  private static getCookieKey(sourceFrom?: string | null): string {
    if (sourceFrom) {
      return `${this.BASE_COOKIE_KEY}_${sourceFrom}`
    }
    return this.BASE_COOKIE_KEY
  }

  /**
   * 保存账号密码
   * @param account 账号
   * @param password 密码
   * @param sourceFrom 来源标识
   */
  static save(account: string, password: string, sourceFrom?: string | null): void {
    try {
      const credentials: RememberedCredentials = {
        account,
        password,
        timestamp: Date.now()
      }

      // 加密整个凭据对象
      const encryptedData = SimpleEncryption.encrypt(JSON.stringify(credentials))

      // 根据来源生成Cookie键名
      const cookieKey = this.getCookieKey(sourceFrom)

      // 保存到Cookie
      const options: Cookies.CookieAttributes = {
        expires: this.EXPIRE_DAYS,
        path: '/',
        sameSite: 'lax'
      }

      // HTTPS 环境下添加 secure 标志
      if (window.location.protocol === 'https:') {
        options.secure = true
      }

      Cookies.set(cookieKey, encryptedData, options)

      console.log(`[CT_REMEMBER_PWD] 密码已保存 (${sourceFrom || 'default'})`)
    } catch (error) {
      console.error('[CT_REMEMBER_PWD] 保存密码失败:', error)
    }
  }

  /**
   * 获取保存的账号密码
   * @param sourceFrom 来源标识
   */
  static get(sourceFrom?: string | null): { account: string; password: string } | null {
    try {
      const cookieKey = this.getCookieKey(sourceFrom)
      const encryptedData = Cookies.get(cookieKey)

      if (!encryptedData) {
        return null
      }

      // 解密数据
      const decryptedData = SimpleEncryption.decrypt(encryptedData)
      if (!decryptedData) {
        this.remove(sourceFrom) // 解密失败，删除无效数据
        return null
      }

      const credentials: RememberedCredentials = JSON.parse(decryptedData)

      // 检查数据完整性
      if (!credentials.account || !credentials.password || !credentials.timestamp) {
        this.remove(sourceFrom)
        return null
      }

      // 检查是否过期
      if (Date.now() - credentials.timestamp > this.MAX_AGE_MS) {
        this.remove(sourceFrom)
        console.log(`[CT_REMEMBER_PWD] 保存的密码已过期 (${sourceFrom || 'default'})`)
        return null
      }

      return {
        account: credentials.account,
        password: credentials.password
      }
    } catch (error) {
      console.warn('[CT_REMEMBER_PWD] 获取保存的密码失败:', error)
      this.remove(sourceFrom) // 发生错误时清除数据
      return null
    }
  }

  /**
   * 删除保存的账号密码
   * @param sourceFrom 来源标识
   */
  static remove(sourceFrom?: string | null): void {
    const cookieKey = this.getCookieKey(sourceFrom)
    Cookies.remove(cookieKey, { path: '/' })
    console.log(`[CT_REMEMBER_PWD] 保存的密码已清除 (${sourceFrom || 'default'})`)
  }

  /**
   * 检查是否有保存的账号密码
   * @param sourceFrom 来源标识
   */
  static exists(sourceFrom?: string | null): boolean {
    return !!this.get(sourceFrom)
  }

  /**
   * 更新密码（保持账号不变）
   * @param newPassword 新密码
   * @param sourceFrom 来源标识
   */
  static updatePassword(newPassword: string, sourceFrom?: string | null): void {
    const saved = this.get(sourceFrom)
    if (saved) {
      this.save(saved.account, newPassword, sourceFrom)
    }
  }

  /**
   * 获取保存密码的时间戳
   * @param sourceFrom 来源标识
   */
  static getSavedTimestamp(sourceFrom?: string | null): number | null {
    try {
      const cookieKey = this.getCookieKey(sourceFrom)
      const encryptedData = Cookies.get(cookieKey)

      if (!encryptedData) {
        return null
      }

      const decryptedData = SimpleEncryption.decrypt(encryptedData)
      if (!decryptedData) {
        return null
      }

      const credentials: RememberedCredentials = JSON.parse(decryptedData)
      return credentials.timestamp || null
    } catch (error) {
      return null
    }
  }

  /**
   * 清除所有来源的记住密码数据
   * 用于登出时清理所有相关数据
   */
  static clearAll(): void {
    // 清除默认的
    this.remove()

    // 清除已知来源的数据
    const knownSources = ['home', 'auth', 'tenant', 'staff', 'client']
    knownSources.forEach(source => {
      this.remove(source)
    })

    console.log('[CT_REMEMBER_PWD] 所有保存的密码已清除')
  }
}

// 导出默认实例
export const rememberPasswordManager = RememberPasswordManager
